import { strict as assert } from "assert"

assert(
  process.env["ENV"],
  "Environment variable ENV not set. Examples: dev | tst1 | stg | uat | prd | john",
)
assert(
  process.env["ENV"].length <= 7,
  `Environment variable ENV value ${process.env["ENV"]} is too long. It cannot be more than 7 characters.`,
)

/**
 * Stages we deploy code too.
 */
export const DeploymentStages = {
  DEV: "dev",
  DEV1: "dev1",
  TST1: "tst1",
  TST2: "tst2",
  UAT: "uat",
  PRD: "prd",
} as const
export type DeploymentStage =
  (typeof DeploymentStages)[keyof typeof DeploymentStages]

/**
 * Typeguard
 */
export const isDeploymentStage = (key: string): key is DeploymentStage => {
  return Object.values(DeploymentStages).includes(key as DeploymentStage)
}

export const DataEnv = "dm"
export const SimulatorEnv = "sim"
/**
 * All known AWS environments.
 */
export enum Stage {
  DEV = "dev",
  DEV1 = "dev1",
  TST1 = "tst1",
  TST2 = "tst2",
  PRD = "prd",
  UAT = "uat",
  TOOLS = "tools",
}

/*
 * APP_NAME_NOT_SET
 * If you encounter 'APP_NAME_NOT_SET_SEE_DOCS' go to where you set your env, and underneath, set NX_TASK_TARGET_PROPJECT TO 'appName'.
 */
export const getAppName = () => {
  return process.env["NX_TASK_TARGET_PROJECT"]
    ? process.env["NX_TASK_TARGET_PROJECT"]
    : "APP_NAME_NOT_SET_SEE_DOCS"
}

export const appName: string = getAppName()

// Env is the suffix we are using
export let env = ""

// stage is the kind of environment we deploy to, like dev or production.
export let stage = ""

export const deploymentStage = (): DeploymentStage => {
  if (isDeploymentStage(stage)) {
    return stage
  } else {
    throw new Error(`Stage ${stage} is not a deployment stage.`)
  }
}

// Tools defines what tools environment in our tools account we will use.
export const toolsEnv: string = process.env["TOOLS_ENV"]
  ? process.env["TOOLS_ENV"]
  : Stage.TOOLS

/**
 * Is the current environment the production environment?
 */
export let isProduction: boolean = stage == Stage.PRD

/**
 * Is the current `stage` the testing/qa environment?
 */
export let isTesting1: boolean = stage == Stage.TST1

/**
 * Is the current `stage` the testing/qa environment?
 */
export let isTesting2: boolean = stage == Stage.TST2

/**
 * Is the current `stage` the dev environment?
 */
export let isDev: boolean = stage == Stage.DEV

/**
 * Is the current `stage` the dev environment?
 */
export let isDev1: boolean = stage == Stage.DEV1

/**
 * Is the current `stage` the UAT environment?
 */
export let isUat: boolean = stage == Stage.UAT

/**
 * Is the current `stage` the tools environment?
 */
export let isTools: boolean = stage == Stage.TOOLS

/**
 * Is the current environment data migration environment?
 */
export const isDataEnv: boolean = env === DataEnv

export const envSpecificName = (name: string): string => {
  return name + "-" + env
}

export const envSpecificBucketName = (name: string): string => {
  return `mpi-aws-tc-${name}-${env}`
}

export const stageSpecificName = (name: string): string => {
  return name + "-" + stage
}

export const toolsEnvSpecificName = (name: string): string => {
  return name + "-" + toolsEnv
}

/**
 * Allow changing `env` and `stage` so our tools setup can to cross account deployments.
 * If Stage is not set or `newEnv` is undefined, we fallback to the "dev" environment.
 */
export const setStage = (newEnv: string) => {
  stage = newEnv
  isProduction = stage == Stage.PRD
  isTesting1 = stage == Stage.TST1
  isTesting2 = stage == Stage.TST2
  isDev = stage == Stage.DEV
  isDev1 = stage == Stage.DEV1
  isTools = stage == Stage.TOOLS
  isUat = stage == Stage.UAT
}

export const setEnv = (newEnv: string) => {
  // Limit env to maximum length of a db cluster identifier
  env = newEnv.substring(0, 63)
}

// Initialisation
export const setDefaultEnv = (defaultEnv: string) => {
  setEnv(process.env["ENV"] || defaultEnv)
  setStage(process.env["STAGE"] || process.env["ENV"] || defaultEnv)
}
setDefaultEnv(Stage.DEV)

export enum Branch {
  trunk = "main",
  prod = "production",
  tst2 = "test2",
  platform = "platform",
  ap_main = "ap_main",
  ff_main = "ff-main",
}

export const repository = "trade-certification-main"

/** Named environments that get production-like infrastructure (proxy, 3 Typesense nodes, etc.). */
export const SPECIAL_ENVS = [
  "hidmain",
  "aptest",
  DataEnv,
  "rstrctd",
  SimulatorEnv,
  "sangp03",
]

export const isSpecialEnv: boolean = SPECIAL_ENVS.includes(env)

/** True only for environments that should get the dedicated EC2 Typesense cluster */
export const shouldDeployEc2Cluster: boolean =
  (stage === Stage.UAT && env === "uat") ||
  (stage === Stage.PRD && env === "prd") ||
  (stage === Stage.DEV && env === "sangp03")
